import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import Container from 'components/Container';
import Icon from 'components/Icon';
import Nav from 'components/Nav';
import ContactForm from 'components/ContactForm';

import {
  Wrapper,
  HeaderContainer,
  BrandContainer,
  BrandTextContainer,
  Brand,
  Tagline,
  NavTopBar,
  ButtonStyles,
  MenuButtonStyles,
  NavSideBar,
} from './Header.styles';

const Header = ({ darkPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sticky, setSticky] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [t] = useTranslation('global');
  const navigate = useNavigate();

  const goHome = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('hero');
      if (element) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  useEffect(() => {
    const handleScroll = () => {
      const { scrollY } = window;
      if (scrollY >= 120 && scrollY < 200) setSticky('-105px');
      if (scrollY >= 200) setSticky('0px');
      if (scrollY < 120) setSticky(null);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Wrapper $isMenuOpen={isMenuOpen} $sticky={sticky} $darkPage={darkPage}>
        <Container>
          <HeaderContainer $isMenuOpen={isMenuOpen}>
            <Button
              styles={MenuButtonStyles}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {!isMenuOpen ? <Icon name="menu" /> : <Icon name="cross" />}
            </Button>
            <BrandContainer onClick={goHome}>
              <svg
                width="40"
                height="40"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id="headerGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: '#7C3AED', stopOpacity: 1 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: '#1E40AF', stopOpacity: 1 }}
                    />
                  </linearGradient>
                  <linearGradient
                    id="headerBorderGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: '#34D399', stopOpacity: 0.8 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: '#A78BFA', stopOpacity: 0.8 }}
                    />
                  </linearGradient>
                </defs>
                <circle
                  cx="16"
                  cy="16"
                  r="15"
                  fill="url(#headerGradient)"
                  stroke="url(#headerBorderGradient)"
                  strokeWidth="1"
                />
                <g transform="translate(6, 6)">
                  <rect
                    x="2"
                    y="2"
                    width="2.5"
                    height="16"
                    fill="#FFFFFF"
                    rx="1.25"
                  />
                  <rect
                    x="4.5"
                    y="2"
                    width="8"
                    height="2.5"
                    fill="#FFFFFF"
                    rx="1.25"
                  />
                  <rect
                    x="4.5"
                    y="8.75"
                    width="6"
                    height="2.5"
                    fill="#FFFFFF"
                    rx="1.25"
                  />
                  <rect
                    x="10"
                    y="4.5"
                    width="2.5"
                    height="4.25"
                    fill="#FFFFFF"
                    rx="1.25"
                  />
                  <rect
                    x="7.5"
                    y="11.25"
                    width="2.5"
                    height="6.75"
                    fill="#FFFFFF"
                    rx="1.25"
                    transform="rotate(30 8.75 14.625)"
                  />
                  <circle
                    cx="15"
                    cy="5"
                    r="1.2"
                    fill="#34D399"
                    style={{
                      animation: 'accentPulse 2s ease-in-out infinite',
                      animationDelay: '0s',
                    }}
                  />
                  <circle
                    cx="15"
                    cy="9"
                    r="0.8"
                    fill="#FBBF24"
                    opacity="0.9"
                    style={{
                      animation: 'accentPulse 2s ease-in-out infinite',
                      animationDelay: '0.7s',
                    }}
                  />
                  <circle
                    cx="15"
                    cy="12.5"
                    r="0.6"
                    fill="#A78BFA"
                    opacity="0.7"
                    style={{
                      animation: 'accentPulse 2s ease-in-out infinite',
                      animationDelay: '1.4s',
                    }}
                  />
                </g>
              </svg>
              <BrandTextContainer>
                <Brand>Redwood Tech</Brand>
                <Tagline>Data Engineering & AI Solutions</Tagline>
              </BrandTextContainer>
            </BrandContainer>
            <NavTopBar>
              <Nav closeMenu={setIsMenuOpen} />
              <Button
                onClick={() => setIsContactOpen(true)}
                styles={ButtonStyles}
              >
                {t('header.contact')}
              </Button>
            </NavTopBar>
          </HeaderContainer>
          {isMenuOpen && (
            <NavSideBar>
              <Nav closeMenu={setIsMenuOpen} />
              <Button
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsContactOpen(true);
                }}
                styles={ButtonStyles}
              >
                {t('header.contact')}
              </Button>
            </NavSideBar>
          )}
        </Container>
      </Wrapper>
      <ContactForm
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </>
  );
};

Header.propTypes = {
  darkPage: PropTypes.bool,
};

export default Header;
